import React, { useState, useEffect, useMemo } from 'react';
import portalAPI, { portalBridgeClubAPI } from '../../services/portalAPI';
import { usePortalLocationOptional } from '../../contexts/PortalLocationContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend
} from 'recharts';

interface OverviewStats {
  total_members: number;
  active_members: number;
  total_revenue: number;
  avg_purchase_frequency: number | null;
}

interface RestockItem {
  product_name: string;
  predicted_quantity: number;
  form_type: string | null;
  urgency: 'normal' | 'medium' | 'high';
  order_by_date: string;
  delivery_date: string;
}

interface TopProduct {
  product_name: string;
  total_quantity: number;
  total_sales: number;
  this_month: number;
  last_month: number;
  trend: 'up' | 'down' | 'stable';
  form_breakdown: Record<string, number>;
}

interface MemberInsight {
  id: number;
  name: string;
  member_code: string;
  last_purchase_date: string | null;
  purchase_count: number;
  avg_frequency: number | null;
  next_purchase_predicted: string | null;
  typical_products: string[];
  total_spent: number;
  loyalty_progress: number;
}

interface TrendData {
  week?: string;
  month?: string;
  purchases: number;
  revenue: number;
  bags: number;
}

interface NonRBProduct {
  product_name: string;
  purchase_count: number;
  total_quantity: number;
  avg_frequency_days: number | null;
}

interface AnalyticsData {
  overview_stats: OverviewStats;
  restock_forecast: RestockItem[];
  top_products: TopProduct[];
  member_insights: MemberInsight[];
  purchase_trends: {
    weekly: TrendData[];
    monthly: TrendData[];
  };
  non_red_bridge_products: NonRBProduct[];
  generated_at: string;
}

interface QRPlacement {
  placement: string;
  label: string;
  scans: number;
  signups: number;
  conversion_rate: number;
}

interface QRAnalyticsData {
  summary: {
    total_scans: number;
    total_signups: number;
    scans_30d: number;
    signups_30d: number;
    conversion_rate: number;
    best_placement: string | null;
  };
  by_placement: QRPlacement[];
  network_comparison: {
    network_avg_conversion: number;
    your_conversion: number;
    performing_above_average: boolean;
    rank: number;
    total_stores: number;
  };
}

interface PortalBridgeClubAnalyticsProps {
  onBuildOrder?: (items: RestockItem[]) => void;
}

const PortalBridgeClubAnalytics: React.FC<PortalBridgeClubAnalyticsProps> = ({ onBuildOrder }) => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSort, setMemberSort] = useState<'spent' | 'recent' | 'frequency'>('spent');
  const [trendView, setTrendView] = useState<'weekly' | 'monthly'>('weekly');
  const [qrData, setQRData] = useState<QRAnalyticsData | null>(null);
  const [qrLoading, setQRLoading] = useState(false);

  // Multi-location support
  const locationContext = usePortalLocationOptional();

  // Reload data when location changes
  useEffect(() => {
    loadAnalytics();
    loadQRAnalytics();
  }, [locationContext?.selectedLocation?.id]);

  const loadQRAnalytics = async () => {
    setQRLoading(true);
    try {
      const response = await portalBridgeClubAPI.getQRAnalytics();
      setQRData(response.data);
    } catch (err) {
      console.error('Error loading QR analytics:', err);
    } finally {
      setQRLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await portalAPI.get('/portal/bridge-club/analytics/');
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = useMemo(() => {
    if (!data?.member_insights) return [];
    let members = [...data.member_insights];

    // Filter by search
    if (memberSearch) {
      const search = memberSearch.toLowerCase();
      members = members.filter(m =>
        (m.name || '').toLowerCase().includes(search) ||
        (m.member_code || '').toLowerCase().includes(search) ||
        (m.typical_products || []).some(p => p.toLowerCase().includes(search))
      );
    }

    // Sort
    if (memberSort === 'spent') {
      members.sort((a, b) => b.total_spent - a.total_spent);
    } else if (memberSort === 'recent') {
      members.sort((a, b) => {
        if (!a.last_purchase_date) return 1;
        if (!b.last_purchase_date) return -1;
        return new Date(b.last_purchase_date).getTime() - new Date(a.last_purchase_date).getTime();
      });
    } else if (memberSort === 'frequency') {
      members.sort((a, b) => (a.avg_frequency || 999) - (b.avg_frequency || 999));
    }

    return members;
  }, [data, memberSearch, memberSort]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const formatDateLong = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>{error}</p>
        <button onClick={loadAnalytics} style={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Bridge Club Analytics</h2>
          <p style={styles.subtitle}>
            {locationContext?.isMultiLocation && (
              <span style={{ fontWeight: 500 }}>
                {locationContext.selectedLocation?.name || 'All Locations'} ·{' '}
              </span>
            )}
            Insights powered by Laura · Updated {formatDateLong(data.generated_at)}
          </p>
        </div>
        <button onClick={loadAnalytics} style={styles.refreshButton}>
          ↻ Refresh
        </button>
      </div>

      {/* Overview Cards */}
      <div style={styles.overviewGrid}>
        <div style={styles.overviewCard}>
          <div style={styles.overviewIcon}>👥</div>
          <div style={styles.overviewValue}>{data.overview_stats?.total_members ?? 0}</div>
          <div style={styles.overviewLabel}>Total Members</div>
        </div>
        <div style={styles.overviewCard}>
          <div style={styles.overviewIcon}>✓</div>
          <div style={styles.overviewValue}>{data.overview_stats?.active_members ?? 0}</div>
          <div style={styles.overviewLabel}>Active (30 days)</div>
        </div>
        <div style={styles.overviewCard}>
          <div style={styles.overviewIcon}>💰</div>
          <div style={styles.overviewValue}>{formatCurrency(data.overview_stats?.total_revenue ?? 0)}</div>
          <div style={styles.overviewLabel}>Total Revenue</div>
        </div>
        <div style={styles.overviewCard}>
          <div style={styles.overviewIcon}>📅</div>
          <div style={styles.overviewValue}>
            {data.overview_stats?.avg_purchase_frequency
              ? `${Math.round(data.overview_stats.avg_purchase_frequency)} days`
              : '—'}
          </div>
          <div style={styles.overviewLabel}>Avg Frequency</div>
        </div>
      </div>

      {/* QR Performance Section */}
      {qrData && (qrData.summary.total_scans > 0 || !qrLoading) && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionTitle}>📱 Your QR Performance</h3>
              <p style={styles.sectionSubtitle}>How your QR code placements are performing</p>
            </div>
          </div>

          {qrLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading QR data...</div>
          ) : qrData ? (
            <>
              {/* Quick Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '1rem',
                marginBottom: '1.5rem',
              }}>
                <div style={{
                  background: '#f0f9ff',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0369a1' }}>
                    {qrData.summary.scans_30d}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>Scans (30d)</div>
                </div>
                <div style={{
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#15803d' }}>
                    {qrData.summary.signups_30d}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>Signups (30d)</div>
                </div>
                <div style={{
                  background: '#fef3c7',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#b45309' }}>
                    {qrData.summary.conversion_rate}%
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>Conversion</div>
                </div>
                <div style={{
                  background: qrData.network_comparison.performing_above_average ? '#dcfce7' : '#fee2e2',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center',
                }}>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: qrData.network_comparison.performing_above_average ? '#166534' : '#991b1b',
                  }}>
                    {qrData.network_comparison.performing_above_average ? '↑' : '↓'}
                    {Math.abs(qrData.summary.conversion_rate - qrData.network_comparison.network_avg_conversion).toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>vs Network Avg</div>
                </div>
              </div>

              {/* Placement Breakdown */}
              {(qrData.by_placement || []).length > 0 && (
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    display: 'flex',
                    padding: '0.75rem 1rem',
                    background: '#f9fafb',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: '#666',
                    borderBottom: '1px solid #e5e7eb',
                  }}>
                    <span style={{ flex: 2 }}>Placement</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>Scans</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>Signups</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>Conv. Rate</span>
                  </div>
                  {qrData.by_placement.map((p, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem 1rem',
                      borderBottom: idx < qrData.by_placement.length - 1 ? '1px solid #f3f4f6' : 'none',
                      fontSize: '0.9rem',
                    }}>
                      <span style={{ flex: 2, fontWeight: 500 }}>{p.label}</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>{p.scans}</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>{p.signups}</span>
                      <span style={{ flex: 1, textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          background: p.conversion_rate >= 10 ? '#dcfce7' : p.conversion_rate >= 5 ? '#fef9c3' : '#f3f4f6',
                          color: p.conversion_rate >= 10 ? '#166534' : p.conversion_rate >= 5 ? '#854d0e' : '#666',
                        }}>
                          {p.conversion_rate}%
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Best Placement Tip */}
              {qrData.summary.best_placement && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  color: '#166534',
                }}>
                  💡 <strong>Best performer:</strong> Your {qrData.summary.best_placement} placement has the highest conversion rate.
                </div>
              )}

              {/* Network Comparison */}
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem 1rem',
                background: '#f9fafb',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#666',
              }}>
                <strong>Network comparison:</strong> Your {qrData.summary.conversion_rate}% conversion rate
                {qrData.network_comparison.performing_above_average
                  ? ` is above the network average of ${qrData.network_comparison.network_avg_conversion}%`
                  : ` is below the network average of ${qrData.network_comparison.network_avg_conversion}%`
                }.
                You rank #{qrData.network_comparison.rank} of {qrData.network_comparison.total_stores} stores.
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              <p>No QR scan data available yet. Place QR codes in your store to start tracking!</p>
            </div>
          )}
        </div>
      )}

      {/* Restock Forecast */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>📦 Restock Forecast</h3>
          <p style={styles.sectionSubtitle}>Laura's predicted demand for the next 2-4 weeks</p>
        </div>

        {(data.restock_forecast || []).length > 0 ? (
          <>
            <div style={styles.forecastTable}>
              <div style={styles.forecastHeader}>
                <span style={{ flex: 2 }}>Product</span>
                <span style={{ flex: 1, textAlign: 'center' }}>Qty</span>
                <span style={{ flex: 1, textAlign: 'center' }}>Form</span>
                <span style={{ flex: 1, textAlign: 'center' }}>Urgency</span>
              </div>
              {(data.restock_forecast || []).slice(0, 8).map((item, idx) => (
                <div key={idx} style={styles.forecastRow}>
                  <span style={{ flex: 2, fontWeight: 500 }}>{item.product_name}</span>
                  <span style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>
                    {item.predicted_quantity}
                  </span>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                    {item.form_type && (
                      <span style={{
                        ...styles.formBadge,
                        backgroundColor: item.form_type === 'pellet' ? '#e3f2fd' : '#fff3e0',
                        color: item.form_type === 'pellet' ? '#1565c0' : '#e65100',
                      }}>
                        {item.form_type}
                      </span>
                    )}
                  </span>
                  <span style={{ flex: 1, textAlign: 'center' }}>
                    <span style={{
                      ...styles.urgencyBadge,
                      backgroundColor: item.urgency === 'high' ? '#ffebee' :
                        item.urgency === 'medium' ? '#fff8e1' : '#e8f5e9',
                      color: item.urgency === 'high' ? '#c62828' :
                        item.urgency === 'medium' ? '#f57f17' : '#2e7d32',
                    }}>
                      {item.urgency === 'high' ? '🔴' : item.urgency === 'medium' ? '🟡' : '🟢'}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            <div style={styles.forecastFooter}>
              <div style={styles.leadTimeInfo}>
                <strong>Lead Time:</strong> Order by {data.restock_forecast?.[0]?.order_by_date ? formatDate(data.restock_forecast[0].order_by_date) : '—'} for delivery by {data.restock_forecast?.[0]?.delivery_date ? formatDate(data.restock_forecast[0].delivery_date) : '—'}
              </div>
              {onBuildOrder && data.restock_forecast && (
                <button
                  onClick={() => onBuildOrder(data.restock_forecast)}
                  style={styles.buildOrderButton}
                >
                  Build Order from Forecast →
                </button>
              )}
            </div>
          </>
        ) : (
          <div style={styles.emptyState}>
            <p>No forecast data available yet. Analytics are calculated based on member purchase history.</p>
          </div>
        )}
      </div>

      {/* Top Products */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>🏆 Top Products</h3>
          <p style={styles.sectionSubtitle}>Best selling Red Bridge products at your store</p>
        </div>

        {(data.top_products || []).length > 0 ? (
          <div style={styles.productsGrid}>
            {(data.top_products || []).slice(0, 6).map((product, idx) => (
              <div key={idx} style={styles.productCard}>
                <div style={styles.productHeader}>
                  <span style={styles.productRank}>#{idx + 1}</span>
                  <span style={styles.trendIcon}>
                    {product.trend === 'up' ? '📈' : product.trend === 'down' ? '📉' : '➡️'}
                  </span>
                </div>
                <div style={styles.productName}>{product.product_name}</div>
                <div style={styles.productStats}>
                  <div>
                    <div style={styles.productStatValue}>{product.total_quantity ?? 0}</div>
                    <div style={styles.productStatLabel}>bags total</div>
                  </div>
                  <div>
                    <div style={styles.productStatValue}>{product.this_month ?? 0}</div>
                    <div style={styles.productStatLabel}>this month</div>
                  </div>
                </div>
                {Object.keys(product.form_breakdown || {}).length > 0 && (
                  <div style={styles.formBreakdown}>
                    {Object.entries(product.form_breakdown || {}).map(([form, qty]) => (
                      <span key={form} style={styles.formTag}>
                        {form}: {qty}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <p>No product data available yet.</p>
          </div>
        )}
      </div>

      {/* Purchase Trends Chart */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h3 style={styles.sectionTitle}>📊 Purchase Trends</h3>
            <p style={styles.sectionSubtitle}>Sales over time</p>
          </div>
          <div style={styles.trendToggle}>
            <button
              onClick={() => setTrendView('weekly')}
              style={{
                ...styles.toggleButton,
                ...(trendView === 'weekly' ? styles.toggleActive : {}),
              }}
            >
              Weekly
            </button>
            <button
              onClick={() => setTrendView('monthly')}
              style={{
                ...styles.toggleButton,
                ...(trendView === 'monthly' ? styles.toggleActive : {}),
              }}
            >
              Monthly
            </button>
          </div>
        </div>

        <div style={styles.chartContainer}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={trendView === 'weekly' ? (data.purchase_trends?.weekly || []) : (data.purchase_trends?.monthly || [])}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey={trendView === 'weekly' ? 'week' : 'month'}
                tickFormatter={(val) => formatDate(val)}
                stroke="#666"
                fontSize={12}
              />
              <YAxis stroke="#666" fontSize={12} />
              <Tooltip
                formatter={(value: number, name: string) => [
                  name === 'revenue' ? formatCurrency(value) : value,
                  name === 'revenue' ? 'Revenue' : name === 'bags' ? 'Bags' : 'Purchases'
                ]}
                labelFormatter={(label) => formatDateLong(label)}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#722F37"
                strokeWidth={2}
                dot={{ fill: '#722F37', strokeWidth: 2 }}
                name="Revenue"
              />
              <Line
                type="monotone"
                dataKey="bags"
                stroke="#508590"
                strokeWidth={2}
                dot={{ fill: '#508590', strokeWidth: 2 }}
                name="Bags"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Member Insights */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h3 style={styles.sectionTitle}>👤 Member Insights</h3>
            <p style={styles.sectionSubtitle}>Individual member analytics</p>
          </div>
          <div style={styles.memberControls}>
            <input
              type="text"
              placeholder="Search members..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              style={styles.searchInput}
            />
            <select
              value={memberSort}
              onChange={(e) => setMemberSort(e.target.value as any)}
              style={styles.sortSelect}
            >
              <option value="spent">Sort: Total Spent</option>
              <option value="recent">Sort: Recent Activity</option>
              <option value="frequency">Sort: Frequency</option>
            </select>
          </div>
        </div>

        <div style={styles.memberTable}>
          <div style={styles.memberTableHeader}>
            <span style={{ flex: 2 }}>Member</span>
            <span style={{ flex: 1, textAlign: 'center' }}>Last Purchase</span>
            <span style={{ flex: 1, textAlign: 'center' }}>Frequency</span>
            <span style={{ flex: 1, textAlign: 'center' }}>Next (Est.)</span>
            <span style={{ flex: 2 }}>Typical Products</span>
            <span style={{ flex: 1, textAlign: 'right' }}>Total Spent</span>
          </div>
          {filteredMembers.slice(0, 20).map((member) => (
            <div key={member.id} style={styles.memberRow}>
              <span style={{ flex: 2 }}>
                <div style={styles.memberName}>{member.name || 'Unknown'}</div>
                <div style={styles.memberCode}>{member.member_code}</div>
              </span>
              <span style={{ flex: 1, textAlign: 'center', color: '#666' }}>
                {member.last_purchase_date ? formatDate(member.last_purchase_date) : '—'}
              </span>
              <span style={{ flex: 1, textAlign: 'center' }}>
                {member.avg_frequency ? `${Math.round(member.avg_frequency)}d` : '—'}
              </span>
              <span style={{ flex: 1, textAlign: 'center' }}>
                {member.next_purchase_predicted ? (
                  <span style={{
                    color: new Date(member.next_purchase_predicted) <= new Date() ? '#c62828' : '#666',
                  }}>
                    {formatDate(member.next_purchase_predicted)}
                  </span>
                ) : '—'}
              </span>
              <span style={{ flex: 2, color: '#666', fontSize: '0.85rem' }}>
                {(member.typical_products || []).slice(0, 2).join(', ') || '—'}
              </span>
              <span style={{ flex: 1, textAlign: 'right', fontWeight: 600 }}>
                {formatCurrency(member.total_spent)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Non-Red Bridge Products */}
      {(data.non_red_bridge_products || []).length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>🛒 Other Products Purchased</h3>
            <p style={styles.sectionSubtitle}>Non-Red Bridge items from receipt scans</p>
          </div>

          <div style={styles.otherProductsGrid}>
            {(data.non_red_bridge_products || []).slice(0, 8).map((product, idx) => (
              <div key={idx} style={styles.otherProductCard}>
                <div style={styles.otherProductName}>{product.product_name}</div>
                <div style={styles.otherProductStats}>
                  <span>{product.total_quantity}x purchased</span>
                  {product.avg_frequency_days && (
                    <span style={styles.frequencyNote}>
                      every ~{Math.round(product.avg_frequency_days)} days
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p style={styles.otherProductsNote}>
            💡 Tip: Track these alongside Red Bridge products for complete inventory planning.
          </p>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem',
    color: '#666',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e0e0e0',
    borderTop: '3px solid #722F37',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '1rem',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '3rem',
  },
  errorText: {
    color: '#c62828',
    marginBottom: '1rem',
  },
  retryButton: {
    background: '#722F37',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    color: '#333',
    fontWeight: 700,
  },
  subtitle: {
    margin: '0.25rem 0 0',
    color: '#666',
    fontSize: '0.9rem',
  },
  refreshButton: {
    background: '#f5f5f5',
    border: '1px solid #ddd',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    color: '#333',
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1rem',
    marginBottom: '2rem',
  },
  overviewCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '1.25rem',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e5e7eb',
  },
  overviewIcon: {
    fontSize: '1.5rem',
    marginBottom: '0.5rem',
  },
  overviewValue: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#722F37',
    marginBottom: '0.25rem',
  },
  overviewLabel: {
    fontSize: '0.85rem',
    color: '#666',
    fontWeight: 500,
  },
  section: {
    background: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e5e7eb',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.25rem',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#333',
  },
  sectionSubtitle: {
    margin: '0.25rem 0 0',
    color: '#666',
    fontSize: '0.85rem',
  },
  forecastTable: {
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  forecastHeader: {
    display: 'flex',
    padding: '0.75rem 1rem',
    background: '#f9fafb',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#666',
    borderBottom: '1px solid #e5e7eb',
  },
  forecastRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '0.9rem',
  },
  formBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'capitalize' as const,
  },
  urgencyBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  forecastFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e5e7eb',
  },
  leadTimeInfo: {
    color: '#666',
    fontSize: '0.9rem',
  },
  buildOrderButton: {
    background: 'linear-gradient(135deg, #508590 0%, #3d6670 100%)',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    color: '#666',
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
  },
  productCard: {
    background: '#f9fafb',
    borderRadius: '8px',
    padding: '1rem',
    border: '1px solid #e5e7eb',
  },
  productHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  productRank: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#722F37',
  },
  trendIcon: {
    fontSize: '1rem',
  },
  productName: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#333',
    marginBottom: '0.75rem',
    lineHeight: 1.3,
  },
  productStats: {
    display: 'flex',
    gap: '1.5rem',
  },
  productStatValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#508590',
  },
  productStatLabel: {
    fontSize: '0.75rem',
    color: '#666',
  },
  formBreakdown: {
    marginTop: '0.75rem',
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
  },
  formTag: {
    background: '#e5e7eb',
    padding: '0.2rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    color: '#666',
  },
  chartContainer: {
    marginTop: '1rem',
  },
  trendToggle: {
    display: 'flex',
    gap: '0.5rem',
  },
  toggleButton: {
    background: '#f5f5f5',
    border: '1px solid #ddd',
    padding: '0.4rem 0.75rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    color: '#666',
  },
  toggleActive: {
    background: '#722F37',
    color: 'white',
    border: '1px solid #722F37',
  },
  memberControls: {
    display: 'flex',
    gap: '0.75rem',
  },
  searchInput: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    width: '200px',
  },
  sortSelect: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    background: 'white',
    cursor: 'pointer',
  },
  memberTable: {
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  memberTableHeader: {
    display: 'flex',
    padding: '0.75rem 1rem',
    background: '#f9fafb',
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#666',
    borderBottom: '1px solid #e5e7eb',
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '0.9rem',
  },
  memberName: {
    fontWeight: 500,
    color: '#333',
  },
  memberCode: {
    fontSize: '0.8rem',
    color: '#999',
  },
  otherProductsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '0.75rem',
  },
  otherProductCard: {
    background: '#f9fafb',
    borderRadius: '8px',
    padding: '0.75rem',
    border: '1px solid #e5e7eb',
  },
  otherProductName: {
    fontWeight: 500,
    fontSize: '0.9rem',
    color: '#333',
    marginBottom: '0.25rem',
  },
  otherProductStats: {
    fontSize: '0.8rem',
    color: '#666',
  },
  frequencyNote: {
    display: 'block',
    color: '#508590',
    marginTop: '0.25rem',
  },
  otherProductsNote: {
    marginTop: '1rem',
    padding: '0.75rem',
    background: '#fffbeb',
    borderRadius: '8px',
    fontSize: '0.85rem',
    color: '#92400e',
  },
};

// Add CSS animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default PortalBridgeClubAnalytics;
